import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { getSixMonthSpendByCurrency } from 'src/app/Home/redux/home.actions'
import {
  selectSixMonthSpendDataCadFromState,
  selectSixMonthSpendDataUsdFromState,
  selectSixMonthSpendLastUpdatedAtFromState,
  selectSixMonthSpendLoadingFromState,
} from 'src/app/Home/redux/home.selectors'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HomepageChartTooltip from 'src/components/HomepageChartTooltip/HomepageChartTooltip'
import LoadingShimmer from 'src/components/LoadingShimmer/LoadingShimmer'
import SpendGraph from 'src/components/SpendGraph/SpendGraph'
import TestId from 'src/components/TestId/TestId'
import { ExtendCurrency, SupportedBrands, svgImageNames } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { useColorOrGrayscaleSVG } from 'src/hooks/brandHooks'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { dateFormatString } from 'src/localization/Dte'
import { getLocaleInfo } from 'src/localization/locales'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { connectedPush } from 'src/navigation/Lnk'
import {
  expensesTransactionsUrl,
  registerAccountUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectBrandName } from 'src/redux/selectors/session.selectors'
import colors from 'src/style/variables.json'
import { toDollars } from 'src/utils/currency.utils'
import { startOfMonth } from 'src/utils/date.utils'
import { css } from 'styled-components'

type TransformedSpendData = {
  x: number
  usd?: number
  usdPhysical?: number
  usdBillPay?: number
  cad?: number
  cadPhysical?: number
  cadBillPay?: number
  fill: string
  month: number
}

interface HomepageChartProps {
  className?: string
  creditCardsCountLoaded: boolean
  relatedToCreditCards: boolean
}

const HomepageChart: React.FC<HomepageChartProps> = (props) => {
  const currencyFormatter = useCurrencyFormatter()
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const locale = useSelector(selectNullSafeLocaleFromState)
  const defaultCurrency = getLocaleInfo(locale).currency
  const sixMonthSpendLoading = useSelector(selectSixMonthSpendLoadingFromState)
  const sixMonthSpendDataUsd = useSelector(selectSixMonthSpendDataUsdFromState)
  const sixMonthSpendDataCad = useSelector(selectSixMonthSpendDataCadFromState)
  const lastUpdatedAt = useSelector(selectSixMonthSpendLastUpdatedAtFromState)
  const { REGISTER_ACCOUNT: canRegisterAccount } =
    useQueryUserFeatures([UserFeature.REGISTER_ACCOUNT]).data || {}
  const brand = useSelector(selectBrandName)

  const localeInfo = getLocaleInfo(locale)

  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()

  const noSpend = useColorOrGrayscaleSVG(svgImageNames.NoSpend)

  const divRef = useRef<HTMLDivElement>(null)
  const upperStatsDivRef = useRef<HTMLDivElement>(null)

  const [chartHeight, setChartHeight] = useState<number | undefined>(undefined)
  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  // 36px each for the upper and lower padding + the height of the upper stats div (36 is the default padding value) + 32px for the bottom margin
  const offsetValue =
    36 * 2 + (upperStatsDivRef.current?.clientHeight || 36) + 32

  // get org created at date start of month because endpoint month values are always the first of the month
  const orgCreatedAtStartOfMonth = new Date(
    startOfMonth(moment(organization?.createdAt).subtract(2, 'months')).format(
      'YYYY-MM-DD',
    ),
  ).getTime()

  const loading = sixMonthSpendLoading || !props.creditCardsCountLoaded

  const totalSixMonthSpend =
    (sixMonthSpendDataUsd?.reduce(
      (acc, data) =>
        acc +
        (data.amountCents || 0) +
        (data.physicalAmountCents || 0) +
        (data.billPayAmountCents || 0),
      0,
    ) || 0) +
    (sixMonthSpendDataCad?.reduce(
      (acc, data) =>
        acc +
        (data.amountCents || 0) +
        (data.physicalAmountCents || 0) +
        (data.billPayAmountCents || 0),
      0,
    ) || 0)

  const filteredSixMonthSpendDataUsd = sixMonthSpendDataUsd
    ?.slice()
    ?.sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime())
    .filter(
      (data) =>
        new Date(data.month).getTime() +
          new Date(data.month).getTimezoneOffset() * 60000 >=
        orgCreatedAtStartOfMonth,
    )
    .map((data) => ({
      month: data.month,
      usd: data.amountCents,
      usdPhysical: data.physicalAmountCents,
      usdBillPay: data.billPayAmountCents,
    }))
  const filteredSixMonthSpendDataCad = sixMonthSpendDataCad
    ?.slice()
    ?.sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime())
    .filter(
      (data) =>
        new Date(data.month).getTime() +
          new Date(data.month).getTimezoneOffset() * 60000 >=
        orgCreatedAtStartOfMonth,
    )
    .map((data) => ({
      month: data.month,
      cad: data.amountCents,
      cadPhysical: data.physicalAmountCents,
      cadBillPay: data.billPayAmountCents,
    }))

  const currentMonthUsdSpend = filteredSixMonthSpendDataUsd
    ? toDollars(
        (filteredSixMonthSpendDataUsd.at(-1)?.usd || 0) +
          (filteredSixMonthSpendDataUsd.at(-1)?.usdPhysical || 0) +
          (filteredSixMonthSpendDataUsd.at(-1)?.usdBillPay || 0),
      )
    : undefined

  const currentMonthCadSpend = filteredSixMonthSpendDataCad
    ? toDollars(
        (filteredSixMonthSpendDataCad.at(-1)?.cad || 0) +
          (filteredSixMonthSpendDataCad.at(-1)?.cadPhysical || 0) +
          (filteredSixMonthSpendDataCad.at(-1)?.cadBillPay || 0),
      )
    : undefined

  const mergedSixMonthSpendData = filteredSixMonthSpendDataUsd
    ? filteredSixMonthSpendDataUsd.map((usdData) => {
        const cadData = filteredSixMonthSpendDataCad?.find(
          (cadData) => cadData.month === usdData.month,
        )
        return {
          month: usdData.month,
          usd: usdData.usd,
          usdPhysical: usdData.usdPhysical,
          usdBillPay: usdData.usdBillPay,
          cad: cadData?.cad,
          cadPhysical: cadData?.cadPhysical,
          cadBillPay: cadData?.cadBillPay,
        }
      })
    : filteredSixMonthSpendDataCad
      ? filteredSixMonthSpendDataCad.map((cadData) => {
          return {
            month: cadData.month,
            usd: undefined,
            usdPhysical: undefined,
            usdBillPay: undefined,
            cad: cadData.cad,
            cadPhysical: cadData.cadPhysical,
            cadBillPay: cadData.cadBillPay,
          }
        })
      : []

  const fill =
    brand === SupportedBrands.Jpm
      ? colors.tokensColorJPM.JPMblue200
      : styleConstants.colors.purple_chart
  const fillPhysical =
    brand === SupportedBrands.Jpm
      ? colors.tokensColorJPM.JPMblue500
      : styleConstants.colors.green
  const fillBillPay =
    brand === SupportedBrands.Jpm
      ? colors.tokensColorJPM.JPMblue100
      : styleConstants.colors.blue_chart

  const transformedSixMonthSpendData: TransformedSpendData[] =
    sixMonthSpendLoading
      ? // fake loading data
        Array.from({ length: 6 }, (_, i) => ({
          x: new Date().setMonth(new Date().getMonth() - (5 - i)),
          usd: 10,
          usdPhysical: 0,
          usdBillPay: 0,
          fill: 'url(#loading)',
          month: i + 1,
        }))
      : !props.relatedToCreditCards
        ? // fake data for no credit cards
          [175000, 99000, 125000, 75000, 110000, 50000].map((usd, i) => ({
            x: new Date().setMonth(new Date().getMonth() - (5 - i)),
            usd: usd,
            usdPhysical: 0,
            usdBillPay: 0,
            fill: 'url(#loading)',
            month: i + 1,
          }))
        : mergedSixMonthSpendData?.map((data, i) => {
            return {
              x:
                new Date(data.month).getTime() +
                new Date(data.month).getTimezoneOffset() * 60000, // accounting for timezone
              usd: data.usd !== undefined ? toDollars(data.usd) : undefined,
              usdPhysical:
                data.usdPhysical !== undefined
                  ? toDollars(data.usdPhysical)
                  : undefined,
              usdBillPay:
                data.usdBillPay !== undefined
                  ? toDollars(data.usdBillPay)
                  : undefined,
              cad: data.cad !== undefined ? toDollars(data.cad) : undefined,
              cadPhysical:
                data.cadPhysical !== undefined
                  ? toDollars(data.cadPhysical)
                  : undefined,
              cadBillPay:
                data.cadBillPay !== undefined
                  ? toDollars(data.cadBillPay)
                  : undefined,
              fill: fill,
              fillPhysical: fillPhysical,
              fillBillPay: fillBillPay,
              month: i + 1,
            }
          })

  const dispatch = useBackgroundDispatch()

  useEffect(() => {
    dispatch(getSixMonthSpendByCurrency())
  }, [])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setChartHeight((65 * viewportHeight.height) / 100 - offsetValue)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight])

  return (
    <div
      css={css`
        height: 100%;
      `}
      ref={divRef}
    >
      <div
        css={css`
          height: 100%;
        `}
      >
        <Box
          css={css`
            min-height: 450px;
            height: 65vh;
            ${!props.relatedToCreditCards &&
            css`
              opacity: 0.5;
            `}
          `}
        >
          <div
            ref={upperStatsDivRef}
            css={css`
              display: flex;
              align-items: flex-start;
              ${getFontCss(
                14,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
              margin-bottom: 32px;
            `}
          >
            <div
              css={css`
                flex-grow: 1;
              `}
            >
              {loading ? (
                <>
                  <LoadingShimmer
                    height={'19px'}
                    width={'160px'}
                    css={css`
                      margin-bottom: 8px;
                    `}
                  />
                  <LoadingShimmer height={'19px'} />
                </>
              ) : (
                <>
                  {translate('Total card spend this month')}
                  {(currentMonthUsdSpend !== undefined ||
                    currentMonthCadSpend !== undefined) && (
                    <div
                      css={css`
                        margin-top: 8px;
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                      `}
                    >
                      {currentMonthUsdSpend !== undefined &&
                        currentMonthUsdSpend !== null && (
                          // redundant font css in these divs in case we decide to change this in the future
                          <div
                            id="currentMonthUsdSpend"
                            css={css`
                              ${getFontCss(
                                20,
                                themeConstants.colors.highlight,
                                styleConstants.fontFamilies.medium,
                              )};
                            `}
                          >
                            {currencyFormatter({
                              amount: currentMonthUsdSpend,
                              currency: localeInfo.currency,
                            })}
                            {currentMonthCadSpend !== undefined &&
                              currentMonthCadSpend !== null && (
                                <span
                                  css={css`
                                    font-size: 12px;
                                  `}
                                >
                                  {` ${ExtendCurrency.Usd}`}
                                </span>
                              )}
                          </div>
                        )}
                      {currentMonthCadSpend !== undefined &&
                        currentMonthCadSpend !== null && (
                          <div
                            id="currentMonthCadSpend"
                            css={css`
                              ${getFontCss(
                                20,
                                themeConstants.colors.highlight,
                                styleConstants.fontFamilies.medium,
                              )};
                            `}
                          >
                            {currencyFormatter({
                              amount: currentMonthCadSpend,
                              currency: localeInfo.currency,
                            })}
                            {currentMonthUsdSpend !== undefined &&
                              currentMonthUsdSpend !== null && (
                                <span
                                  css={css`
                                    font-size: 12px;
                                  `}
                                >
                                  {` ${ExtendCurrency.Cad}`}
                                </span>
                              )}
                          </div>
                        )}
                    </div>
                  )}
                </>
              )}
            </div>
            {props.relatedToCreditCards && totalSixMonthSpend > 0 && (
              <>
                {loading ? (
                  <div>
                    <LoadingShimmer
                      height={'19px'}
                      css={css`
                        margin-bottom: 6px;
                        margin-left: auto;
                        margin-right: 0;
                      `}
                    />
                    <LoadingShimmer height={'19px'} width={'160px'} />
                  </div>
                ) : (
                  <div
                    css={css`
                      text-align: right;
                      ${getFontCss(
                        12,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )}
                    `}
                  >
                    <div>
                      <TestId testId={'chartLastUpdatedOnString'}>
                        {translate('Last updated on')}
                      </TestId>
                    </div>
                    <div>
                      <TestId testId={'chartLastUpdatedDate'}>
                        {translate('${date} at ${time}', {
                          date: moment(lastUpdatedAt).format('MMM DD, YYYY'),
                          time: moment(lastUpdatedAt).format('h:mm A'),
                        })}
                      </TestId>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <SpendGraph
            height={Math.max(chartHeight || 300, 300)}
            graphData={transformedSixMonthSpendData}
            currency={defaultCurrency}
            xAxisProps={{
              ticks: true,
              tickFormatter: (val: number): string => moment(val).format('MMM'),
              domain:
                !props.relatedToCreditCards || totalSixMonthSpend === 0
                  ? [dateFormatString(moment().format(), 'M')]
                  : undefined,
            }}
            yAxisProps={{
              tickFormatter: (val: number): string =>
                currencyFormatter({
                  amount: val,
                  maximumFractionDigits: 0,
                  notation: 'compact',
                }),
              domain:
                !props.relatedToCreditCards || totalSixMonthSpend === 0
                  ? [0, 200000]
                  : undefined,
            }}
            barProps={{
              radius: [5, 5, 0, 0],
              size: 400 / transformedSixMonthSpendData.length,
            }}
            tooltip={
              props.relatedToCreditCards && totalSixMonthSpend > 0 ? (
                <HomepageChartTooltip
                  active={false}
                  payload={[]}
                  label=""
                  fill={fill}
                  fillPhysical={fillPhysical}
                  fillBillPay={fillBillPay}
                />
              ) : undefined
            }
            onClick={(data: unknown): void => {
              const castedData = data as { payload: TransformedSpendData }
              dispatch(
                connectedPush({
                  pathname: expensesTransactionsUrl(),
                  search:
                    transformedSixMonthSpendData.length >
                    castedData.payload.month
                      ? `?type=all&since=${moment(castedData.payload.x).format('YYYY-MM-DD')}&until=${moment(castedData.payload.x).endOf('month').format('YYYY-MM-DD')}&status=CLEARED&dateType=Clear`
                      : `?type=all&since=${moment(castedData.payload.x).format('YYYY-MM-DD')}&status=CLEARED&dateType=Clear`,
                  state: {},
                  hash: '',
                }),
              )
            }}
            defs={
              <defs>
                <linearGradient id="loading" x1="0" y1="0" x2="0" y2="2">
                  <stop
                    offset="5%"
                    stopColor={styleConstants.colors.gray_medium}
                    stopOpacity={1}
                  />
                  <stop
                    offset="95%"
                    stopColor={styleConstants.colors.gray_lightest}
                    stopOpacity={1}
                  />
                </linearGradient>
              </defs>
            }
          />
        </Box>
        {!loading && (
          <div
            css={css`
              position: absolute;
              top: 40%;
              left: 50%;
              transform: translate(-50%, -50%);
              text-align: center;
              width: 85%;
              margin: 0px 20px;
            `}
          >
            {!props.relatedToCreditCards ? (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                `}
              >
                <div
                  css={css`
                    margin-bottom: 24px;
                  `}
                >
                  {noSpend}
                </div>
                <TestId
                  testId={'chartNoAccountEmptyStateMessage'}
                  css={css`
                    margin-bottom: 16px;
                  `}
                >
                  <span
                    css={css`
                      ${getFontCss(
                        16,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )}
                    `}
                  >
                    {translate('This is where your spend history will appear')}
                  </span>
                </TestId>
                {canRegisterAccount && (
                  <>
                    <TestId
                      testId={'chartAddAccountMessage'}
                      css={css`
                        margin-bottom: 32px;
                      `}
                    >
                      {translate('Add an account and start spending!')}
                    </TestId>
                    <TestId testId={'chartAddAccountBtn'}>
                      <Button
                        design={ButtonDesigns.designCta}
                        type={ButtonTypes.button}
                        text={translate('Add an account')}
                        onClick={(): void => {
                          analytics?.track(
                            'home page chart - add an account CTA',
                            { screen: '/home' },
                          )
                        }}
                        linkProps={{
                          to: registerAccountUrl(),
                          ariaLabelSource: 'Review the Invite',
                        }}
                      />
                    </TestId>
                  </>
                )}
              </div>
            ) : (
              <>
                {totalSixMonthSpend === 0 && (
                  <TestId testId={'chartNoTxnDataEmptyStateMessage'}>
                    {translate(
                      'No transaction data found. Start spending with your credit card',
                    )}
                  </TestId>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default HomepageChart
